import { ModalRef, useNavigation } from '@msdk/common'
import {
  CustomActionDefinition,
  EntityAcl,
  EntityContext,
  EntityModel,
  EntityOperation,
  FormMode,
  FormModel,
  FormNode,
} from '@msdk/definitions'
import { ButtonIcon, COLORS, createComponentWrapperWithRef, Menu, MoreVertIcon, styled, uiTokens } from '@msdk/ui'
import React, { MouseEvent, Suspense, useState } from 'react'

import { CustomLogicInvoker } from '../DynamicForm/custom-logic-invoker'
import { FormActions } from '../FormActions'
import { useEnableFormActions } from './filter-form-action.hook'

const MuiMenuIconButton = styled(ButtonIcon)`
  padding-top: 0;
  padding-bottom: 0;
  color: ${COLORS.primary.main};
`
const NovaMenuIconButton = styled(ButtonIcon)`
  margin-left: auto;
  margin-right: ${uiTokens.spacing.padding.medium}px;
`
const MenuIconButton = createComponentWrapperWithRef(
  {
    mui: MuiMenuIconButton,
    nova: NovaMenuIconButton as React.ElementType<React.ComponentProps<typeof ButtonIcon>>,
  },
  'MenuIconButton',
)

interface Props {
  onSave: (actionId?: string | undefined) => Promise<EntityOperation>
  onDelete: () => Promise<EntityOperation>
  onRestore: () => Promise<EntityOperation>
  onReloadEntity: () => Promise<void>
  onMoveToBin: () => Promise<EntityOperation>
  rebindEntitiy: (entity: EntityContext) => void
  entity: EntityContext
  entityModel: EntityModel
  formModel: FormModel
  rootNode: FormNode
  acl: EntityAcl
  invoker: CustomLogicInvoker
  mode: FormMode
  entityLoaded: boolean
  modalRef?: ModalRef
  onModeChange?: (mode: FormMode, formName: string) => void
}

export function HeaderActions(props: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const frameworkNavigate = useNavigation()

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const enableActions = useEnableFormActions(
    props.entityModel.name,
    props.entity,
    props.formModel.name,
    props.formModel.customActions as CustomActionDefinition[],
    props.mode,
    props.acl,
    props.rootNode,
    frameworkNavigate,
    props.onSave,
    props.onReloadEntity,
    props.entityLoaded,
    props.rebindEntitiy,
  )

  const hasActions = enableActions.totalVisibleActionCount > 0
  const shouldShowDirectActions = enableActions.totalVisibleActionCount === 1

  return (
    <>
      {hasActions && (
        <>
          {shouldShowDirectActions ? (
            <FormActions
              acl={props.acl}
              delete={props.onDelete}
              entity={props.entity}
              entityName={props.entityModel.name}
              formName={props.formModel.name}
              formNode={props.rootNode}
              invoker={props.invoker}
              moveToBin={props.onMoveToBin}
              navigate={frameworkNavigate}
              reloadEntity={props.onReloadEntity}
              restore={props.onRestore}
              save={props.onSave}
              visualization="SingleAction"
              mode={props.mode}
              handleClose={handleClose}
              enabledFormActions={enableActions}
              modalRef={props.modalRef}
              onModeChange={props.onModeChange}
            />
          ) : (
            <>
              <MenuIconButton onClick={handleClick}>
                <MoreVertIcon />
              </MenuIconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                <Suspense>
                  <FormActions
                    acl={props.acl}
                    delete={props.onDelete}
                    entity={props.entity}
                    entityName={props.entityModel.name}
                    formName={props.formModel.name}
                    formNode={props.rootNode}
                    invoker={props.invoker}
                    moveToBin={props.onMoveToBin}
                    navigate={frameworkNavigate}
                    reloadEntity={props.onReloadEntity}
                    restore={props.onRestore}
                    save={props.onSave}
                    visualization="MenuItems"
                    mode={props.mode}
                    handleClose={handleClose}
                    enabledFormActions={enableActions}
                    modalRef={props.modalRef}
                    onModeChange={props.onModeChange}
                  />
                </Suspense>
              </Menu>
            </>
          )}
        </>
      )}
    </>
  )
}
